import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import ApiService from '../../../services/ApiService';
import Loader from '../../../components/Loader';
import { formatDate } from '../../../utils/helper';

const BookingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    // go back to the previous page if possible, otherwise fall back to the list
    const goBack = () => navigate(-1);
    const [isLoader, setIsLoader] = useState(false);
    const [booking, setBooking] = useState(null);
    const [statusData, setStatusData] = useState({
        payment_status: '',
        notes: ''
    });
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusUpdateData, setStatusUpdateData] = useState({
        status: '',
        payment_status: '',
        notes: ''
    });
    const [review, setReview] = useState(null);
    const [showReviewTab, setShowReviewTab] = useState(false);

    useEffect(() => {
        getBookingDetails();
    }, [id]);

    useEffect(() => {
        if (booking && booking.is_reviewed && booking.review_id) {
            getReviewDetails();
        }
    }, [booking]);

    const getBookingDetails = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: `admin/bookings/${id}`
            });

            if (response.data.status) {
                setBooking(response.data.data);
                setStatusData({
                    payment_status: response.data.data.payment_status,
                    notes: response.data.data.notes || ''
                });
                setStatusUpdateData({
                    status: response.data.data.status,
                    payment_status: response.data.data.payment_status,
                    notes: response.data.data.notes || ''
                });
            } else {
                toast.error(response.data.message);
                navigate('/admin/bookings');
            }
        } catch (error) {
            toast.error('Failed to fetch booking details');
            console.error("Error fetching booking details:", error);
            navigate('/admin/bookings');
        } finally {
            setIsLoader(false);
        }
    };

    const getNextStatus = (currentStatus) => {
        const statusFlow = {
            'Pending': 'Confirmed',
            'pending': 'Confirmed',
            'Confirmed': 'Completed',
            'confirmed': 'Completed',
        };
        return statusFlow[currentStatus] || null;
    };

    const getNextStatusName = (currentStatus) => {
        const names = { 'Pending': 'Confirm Booking', 'pending': 'Confirm Booking', 'Confirmed': 'Mark Complete', 'confirmed': 'Mark Complete' };
        return names[currentStatus] || null;
    };

    const getNextStatusButtonClass = (currentStatus) => {
        const classes = { 'Pending': 'btn-light-success', 'pending': 'btn-light-success', 'Confirmed': 'btn-light-success', 'confirmed': 'btn-light-success' };
        return classes[currentStatus] || null;
    };

    const handleNextStatus = async () => {
        const currentStatus = booking.status;
        const nextStatus = getNextStatus(currentStatus);
        
        if (!nextStatus) {
            toast.error('No next status available for this booking');
            return;
        }

        const nextStatusName = getNextStatusName(currentStatus);
        
        const result = await Swal.fire({
            title: 'Update Booking Status',
            text: `Are you sure you want to ${nextStatusName.toLowerCase()} this booking?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4BC95F',
            confirmButtonText: `Yes, ${nextStatusName}`,
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                const updateData = {
                    status: nextStatus,
                    payment_status: statusData.payment_status,
                    notes: statusData.notes
                };

                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/bookings/${id}/status`,
                    data: updateData
                });

                if (response.data.status) {
                    toast.success(`Booking ${nextStatusName.toLowerCase()}d successfully`);
                    getBookingDetails();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error('Failed to update booking status');
                console.error("Error updating booking status:", error);
            }
        }
    };

    const handleAcceptBooking = async () => {
        const result = await Swal.fire({
            title: 'Accept Booking',
            text: 'Are you sure you want to accept this booking?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, Accept',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                const updateData = {
                    status: 'Confirmed',
                    payment_status: statusData.payment_status,
                    notes: statusData.notes
                };

                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/bookings/${id}/status`,
                    data: updateData
                });

                if (response.data.status) {
                    toast.success('Booking accepted successfully');
                    getBookingDetails();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error('Failed to accept booking');
                console.error("Error accepting booking:", error);
            }
        }
    };

    const handleRejectBooking = async () => {
        const result = await Swal.fire({
            title: 'Reject Booking',
            text: 'Are you sure you want to reject this booking? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Yes, Reject',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                const updateData = {
                    status: 'Cancelled',
                    payment_status: statusData.payment_status,
                    notes: statusData.notes
                };

                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/bookings/${id}/status`,
                    data: updateData
                });

                if (response.data.status) {
                    toast.success('Booking rejected successfully');
                    getBookingDetails();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error('Failed to reject booking');
                console.error("Error rejecting booking:", error);
            }
        }
    };

    const handleCancelBooking = async () => {
        const result = await Swal.fire({
            title: 'Cancel Booking',
            text: 'Are you sure you want to cancel this booking? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, Cancel Booking',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No, Keep Booking',
        });

        if (result.isConfirmed) {
            try {
                const updateData = {
                    status: 'Cancelled',
                    payment_status: statusData.payment_status,
                    notes: statusData.notes
                };

                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/bookings/${id}/status`,
                    data: updateData
                });

                if (response.data.status) {
                    toast.success('Booking cancelled successfully');
                    getBookingDetails();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error('Failed to cancel booking');
                console.error("Error cancelling booking:", error);
            }
        }
    };

    const openStatusModal = () => {
        setShowStatusModal(true);
    };

    const closeStatusModal = () => {
        setShowStatusModal(false);
    };

    const handleStatusUpdateChange = (e) => {
        const { name, value } = e.target;
        setStatusUpdateData(prev => ({ ...prev, [name]: value }));
    };

    const updateBookingStatus = async () => {
        // Check if status is actually changing
        if (statusUpdateData.status === booking.status && 
            statusUpdateData.payment_status === booking.payment_status && 
            statusUpdateData.notes === (booking.notes || '')) {
            toast.info('No changes detected');
            return;
        }

        const result = await Swal.fire({
            title: 'Update Booking Status',
            text: `Are you sure you want to update this booking status?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4BC95F',
            confirmButtonText: 'Yes, Update Status',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                const response = await ApiService.request({
                    method: "POST",
                    url: `admin/bookings/${id}/status`,
                    data: statusUpdateData
                });

                if (response.data.status) {
                    toast.success('Booking status updated successfully');
                    getBookingDetails();
                    closeStatusModal();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error('Failed to update booking status');
                console.error("Error updating booking status:", error);
            }
        }
    };


    const deleteBooking = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'You will not be able to revert this!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            try {
                const response = await ApiService.request({
                    method: "DELETE",
                    url: `admin/bookings/${id}`
                });

                if (response.data.status) {
                    toast.success('Booking deleted successfully');
                    navigate('/admin/bookings');
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error('Failed to delete booking');
                console.error("Error deleting booking:", error);
            }
        }
    };

    const getReviewDetails = async () => {
        try {
            const response = await ApiService.request({
                method: "GET",
                url: `admin/reviews/${booking.review_id}`
            });

            if (response.data.status) {
                setReview(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching review details:", error);
        }
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            pending: 'badge-light-warning',
            confirmed: 'badge-light-info',
            completed: 'badge-light-success',
            cancelled: 'badge-light-danger',
        };
        return `badge ${statusClasses[status?.toLowerCase()] || 'badge-light-secondary'}`;
    };

    const getPaymentStatusBadge = (status) => {
        const statusClasses = {
            pending: 'badge-light-warning',
            paid: 'badge-light-success',
            failed: 'badge-light-danger',
            refunded: 'badge-light-info'
        };
        return `badge ${statusClasses[status?.toLowerCase()] || 'badge-light-secondary'}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoader && !booking) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Loader text="Loading Booking Details..." size="40" />
            </div>
        );
    }

    if (!booking) {
        return null;
    }

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Booking Details - #{booking._id.slice(-8)}
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/bookings" className="text-muted text-hover-primary">Bookings</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Details</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <button type="button" className="btn btn-sm btn-secondary" onClick={goBack}>
                            <i className="bi bi-arrow-left me-1"></i>
                            Back
                        </button>
                        {booking.status === 'pending' && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleAcceptBooking}
                                >
                                    <i className="bi bi-check-circle me-1"></i>
                                    Accepted
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleRejectBooking}
                                >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Reject
                                </button>
                            </>
                        )}
                        {booking.status !== 'pending' && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-info"
                                onClick={openStatusModal}
                            >
                                <i className="bi bi-pencil-fill me-1"></i>
                                Update Status
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-light-danger"
                            onClick={deleteBooking}
                        >
                            <i className="bi bi-trash-fill me-1"></i>
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    {/* Booking Overview Card */}
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header cursor-pointer">
                            <div className="card-title m-0">
                                <h3 className="fw-bold m-0">Booking Overview</h3>
                            </div>
                        </div>
                        <div className="card-body p-9">
                            <div className="row mb-7">
                                <div className="col-lg-3 col-md-4 col-sm-6">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="symbol symbol-100px symbol-circle mb-7">
                                            <div className="symbol-label bg-light-primary">
                                                <i className="bi bi-calendar-check fs-2x text-primary"></i>
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <h3 className="fw-bold fs-4 mb-1">#{booking._id.slice(-8)}</h3>
                                            <div className={`badge ${getStatusBadge(booking.status)} fs-7`}>
                                                {booking.status?.replace('_', ' ').toUpperCase()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-9 col-md-8 col-sm-6">
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span className="required">Booking Date</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{formatDate(booking.event_date || booking.booking_date)}</span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Booking Time</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{booking.event_time || booking.booking_time}</span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Total Amount</span>
                                                </label>
                                                <span className="fs-6 text-gray-800 fw-bold text-primary">AED {booking.grand_total ?? booking.total_amount}</span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className=" mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Payment Status</span>
                                                </label>
                                                <span className={`badge ${getPaymentStatusBadge(booking.payment_status)} fs-7`}>
                                                    {booking.payment_status?.toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Payment Method</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{booking.payment_method?.toUpperCase()}</span>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Created Date</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">
                                                    {formatDate(booking.createdAt, 'DD MMM YYYY, hh:mm A')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer and Shop Information */}
                    <div className="row g-5 g-xl-8 mb-5">
                        <div className="col-xl-6">
                            <div className="card card-xl-stretch mb-xl-8">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Services</h3>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    {(booking.services || booking.service_ids || []).length > 0 ? (
                                        (booking.services || booking.service_ids).map((service, index) => (
                                            <div key={index} className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                                                <div className="flex-grow-1">
                                                    <h6 className="fw-bold mb-1">{service.title || service.name || JSON.stringify(service)}</h6>
                                                    {service.description && <p className="text-muted mb-0 fs-7">{service.description}</p>}
                                                    {service.estimated_time && <small className="text-muted">{service.estimated_time}</small>}
                                                </div>
                                                <div className="text-end">
                                                    {service.price != null && <span className="fw-bold text-primary">AED {service.price}{service.price_type && <span className="text-muted fw-normal"> / {service.price_type === 'per_hour' ? 'hour' : 'day'}</span>}</span>}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No services listed</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-6">
                            <div className="card card-xl-stretch mb-xl-8">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Customer Information</h3>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    <div className="d-flex flex-column mb-7 fv-row">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">Name</span>
                                        </label>
                                        <span className="fs-6 text-gray-800">{booking.user_id?.name || 'N/A'}</span>
                                    </div>
                                    <div className="d-flex flex-column mb-7 fv-row">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span>Email</span>
                                        </label>
                                        <span className="fs-6 text-gray-800">{booking.user_id?.email || 'N/A'}</span>
                                    </div>
                                    <div className="d-flex flex-column mb-7 fv-row">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span>Phone</span>
                                        </label>
                                        <span className="fs-6 text-gray-800">{booking.user_id?.phone || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Services and Team Member */}
                    <div className="row g-5 g-xl-8 mb-5">
                        
                        
                        <div className="col-xl-6">
                            <div className="card card-xl-stretch mb-xl-8">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Shop Information</h3>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    <div className="d-flex flex-column mb-7 fv-row">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">partner Name</span>
                                        </label>
                                        <span className="fs-6 text-gray-800">
                                            {booking.partner_id?.company_name || booking.shop_id?.name || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column mb-7 fv-row">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span>Address</span>
                                        </label>
                                        <span className="fs-6 text-gray-800">
                                            {booking.address_id?.complete_address || booking.partner_id?.address || booking.shop_id?.location || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column mb-7 fv-row">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span>Phone</span>
                                        </label>
                                        <span className="fs-6 text-gray-800">
                                            {booking.partner_id?.phone || booking.shop_id?.phone || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-6">
                            <div className="card card-xl-stretch mb-xl-8">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Team Member</h3>
                                    </div>
                                </div>
                                <div className="card-body p-9">
                                    {booking.team_member_id ? (
                                        <div>
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span className="required">Name</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{booking.team_member_id.name}</span>
                                            </div>
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Position</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{booking.team_member_id.position || 'N/A'}</span>
                                            </div>
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Email</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{booking.team_member_id.email}</span>
                                            </div>
                                            <div className="d-flex flex-column mb-7 fv-row">
                                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                                    <span>Phone</span>
                                                </label>
                                                <span className="fs-6 text-gray-800">{booking.team_member_id.phone}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="bi bi-person-x fs-2x text-muted mb-3"></i>
                                            <p className="text-muted">No team member assigned</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Financial Details */}
                    {booking.promo_discount > 0 && (
                        <div className="row g-5 g-xl-8 mb-5">
                            <div className="col-xl-12">
                                <div className="card card-xl-stretch mb-xl-8">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <h3 className="fw-bold m-0">Financial Details</h3>
                                        </div>
                                    </div>
                                    <div className="card-body p-9">
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="d-flex flex-column text-center">
                                                    <span className="fs-6 fw-semibold text-muted mb-2">Subtotal</span>
                                                    <span className="fs-3 fw-bold text-gray-800">AED {booking.total_amount}</span>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="d-flex flex-column text-center">
                                                    <span className="fs-6 fw-semibold text-muted mb-2">Discount</span>
                                                    <span className="fs-3 fw-bold text-success">-AED {booking.promo_discount}</span>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="d-flex flex-column text-center">
                                                    <span className="fs-6 fw-semibold text-muted mb-2">Grand Total</span>
                                                    <span className="fs-3 fw-bold text-primary">AED {booking.grand_total ?? booking.total_amount}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {booking.promo_code && (
                                            <div className="mt-5 text-center">
                                                <span className="badge badge-light-info fs-6">Promo Code: {booking.promo_code}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    {booking.notes && (
                        <div className="row g-5 g-xl-8 mb-5">
                            <div className="col-xl-12">
                                <div className="card card-xl-stretch mb-xl-8">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <h3 className="fw-bold m-0">Notes</h3>
                                        </div>
                                    </div>
                                    <div className="card-body p-9">
                                        <p className="fs-6 text-gray-800">{booking.notes}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Review Tab */}
                    <div className="row g-5 g-xl-8 mb-5">
                        <div className="col-xl-12">
                            <div className="card card-xl-stretch mb-xl-8">
                                <div className="card-header">
                                    <div className="card-title">
                                        <h3 className="fw-bold m-0">Review & Rating</h3>
                                    </div>
                                    <div className="card-toolbar">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-primary"
                                            onClick={() => setShowReviewTab(!showReviewTab)}
                                        >
                                            <i className={`bi ${showReviewTab ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                                            {showReviewTab ? 'Hide' : 'Show'} Review
                                        </button>
                                    </div>
                                </div>
                                {showReviewTab && (
                                    <div className="card-body p-9">
                                        {booking.is_reviewed && review ? (
                                            <div>
                                                {/* Shop Review */}
                                                <div className="row mb-8">
                                                    <div className="col-md-6">
                                                        <div className="card bg-light-primary">
                                                            <div className="card-body p-6">
                                                                <div className="d-flex align-items-center mb-4">
                                                                    <div className="symbol symbol-50px me-4">
                                                                        <img src={booking.shop_id?.image || '/blank.png'} alt="Shop" className="w-100" />
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="fw-bold mb-1">Shop Review</h4>
                                                                        <div className="d-flex align-items-center">
                                                                            {[...Array(5)].map((_, i) => (
                                                                                <i
                                                                                    key={i}
                                                                                    className={`bi bi-star-fill fs-3 me-1 ${
                                                                                        i < review.shop_rating ? 'text-warning' : 'text-muted'
                                                                                    }`}
                                                                                ></i>
                                                                            ))}
                                                                            <span className="ms-2 fw-bold fs-5">{review.shop_rating}/5</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {review.shop_comment && (
                                                                    <p className="text-gray-800 mb-0">"{review.shop_comment}"</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Team Member Review */}
                                                    {review.team_member_id && (review.team_rating || review.team_comment) && (
                                                        <div className="col-md-6">
                                                            <div className="card bg-light-success">
                                                                <div className="card-body p-6">
                                                                    <div className="d-flex align-items-center mb-4">
                                                                        <div className="symbol symbol-50px me-4">
                                                                            <img src={booking.team_member_id?.image || '/blank.png'} alt="Team Member" className="w-100" />
                                                                        </div>
                                                                        <div>
                                                                            <h4 className="fw-bold mb-1">Team Member Review</h4>
                                                                            <div className="d-flex align-items-center">
                                                                                {[...Array(5)].map((_, i) => (
                                                                                    <i
                                                                                        key={i}
                                                                                        className={`bi bi-star-fill fs-3 me-1 ${
                                                                                            i < (review.team_rating || 0) ? 'text-warning' : 'text-muted'
                                                                                        }`}
                                                                                    ></i>
                                                                                ))}
                                                                                <span className="ms-2 fw-bold fs-5">{review.team_rating || 0}/5</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {review.team_comment && (
                                                                        <p className="text-gray-800 mb-0">"{review.team_comment}"</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Tip Information */}
                                                {review.tip_amount > 0 && (
                                                    <div className="row mb-6">
                                                        <div className="col-12">
                                                            <div className="card bg-light-info">
                                                                <div className="card-body p-6">
                                                                    <div className="d-flex align-items-center">
                                                                        <i className="bi bi-gift-fill fs-2x text-primary me-3"></i>
                                                                        <div>
                                                                            <h5 className="fw-bold mb-1">Tip Amount</h5>
                                                                            <span className="fs-3 fw-bold text-primary">${review.tip_amount}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Review Details */}
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="d-flex flex-column">
                                                            <span className="fs-6 fw-semibold text-muted mb-2">Review Date</span>
                                                            <span className="fs-6 fw-bold text-gray-800">
                                                                {formatDate(review.createdAt, 'DD MMM YYYY, hh:mm A')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="d-flex flex-column">
                                                            <span className="fs-6 fw-semibold text-muted mb-2">Same Review for Team</span>
                                                            <span className="fs-6 fw-bold text-gray-800">
                                                                {review.same_review_for_team ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : booking.status === 'completed' ? (
                                            <div className="text-center py-10">
                                                <i className="bi bi-star fs-3x text-muted mb-5"></i>
                                                <h4 className="fw-bold text-muted mb-3">No Review Yet</h4>
                                                <p className="text-muted fs-6">This booking has been completed but the customer hasn't left a review yet.</p>
                                            </div>
                                        ) : (
                                            <div className="text-center py-10">
                                                <i className="bi bi-clock fs-3x text-muted mb-5"></i>
                                                <h4 className="fw-bold text-muted mb-3">Review Not Available</h4>
                                                <p className="text-muted fs-6">Reviews are only available for completed bookings.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Status Update Modal */}
            {showStatusModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Update Booking Status</h5>
                                <button type="button" className="btn-close" onClick={closeStatusModal}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Booking Status</label>
                                    <select
                                        className="form-control"
                                        name="status"
                                        value={statusUpdateData.status}
                                        onChange={handleStatusUpdateChange}
                                    >
                                        <option value="Pending">Pending</option>
                                        <option value="Confirmed">Confirmed</option>
                                        <option value="Cancelled">Cancelled</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                    <div className="form-text">
                                        <small className="text-muted">
                                            Current status: <span className={`badge ${getStatusBadge(booking.status)}`}>
                                                {booking.status?.replace('_', ' ').toUpperCase()}
                                            </span>
                                        </small>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Payment Status</label>
                                    <select
                                        className="form-control"
                                        name="payment_status"
                                        value={statusUpdateData.payment_status}
                                        onChange={handleStatusUpdateChange}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="paid">Paid</option>
                                        <option value="failed">Failed</option>
                                        <option value="refunded">Refunded</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Notes</label>
                                    <textarea
                                        className="form-control"
                                        name="notes"
                                        value={statusUpdateData.notes}
                                        onChange={handleStatusUpdateChange}
                                        rows="3"
                                        placeholder="Add notes..."
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeStatusModal}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={updateBookingStatus}>
                                    Update Status
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default BookingDetails;
